import React, { useState, useEffect } from 'react';
import { inWorkingOrder } from '../../utils/stopOrder';
import { useQuery } from 'react-query';
import { api } from '../../services/api';
import { payOf, splitEarned, notCountedWarning } from '../../utils/crewEarned';
import { useAuth } from '../../hooks/useAuth';
import { DollarSign, Clock, CheckCircle, Zap, X } from 'lucide-react';
import JudgedOnThis from '../../components/crew/JudgedOnThis';
import { businessToday, isoPlusDays } from '../../utils/time';
import { weekStartOf } from '../../utils/payWindow';
import { placeLabel } from '../../utils/format';

function todayISO() { return businessToday(); }

// The Mon–Sun pay week in Austin — the same week the pay ledger card below
// counts. This used to be Sunday–Saturday on the phone's own clock, so the
// two cards disagreed about which jobs were "this week" every Sunday.
// Once the owner pays monthly (Settings → Crew & pay → Crew pay window, Oct 27
// 2026) GET /crew-pay/me answers with the calendar month instead — 1st to last
// day, paid the 1st after — and the card follows it: one monthly total, the
// ledger's weeks inside it, "Earned this month". utils/payWindow.ts has the rule.
function weekRange() {
  const start = weekStartOf(businessToday());
  return { start, end: isoPlusDays(start, 6) };
}

function fmtDate(iso: string) {
  return new Date(iso + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
}

function fmtShortDate(iso: string) {
  return new Date(iso + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

// One row of the week's pay: a ledger line (owed / sent / confirmed), a stop
// still to do, or — only when this login has no ledger — a finished stop at its estimate.
type LineStatus = 'owed' | 'sent' | 'confirmed' | 'todo' | 'est';
type Line = { key: string; day: string; title: string; words: string; amount: number; status: LineStatus };
const STATUS_WORD: Record<LineStatus, string> = { owed: 'owed', sent: 'sent', confirmed: 'confirmed', todo: 'to do', est: 'estimate' };
const statusCls = (s: LineStatus) =>
  s === 'owed' ? 'text-amber-700' : s === 'sent' ? 'text-blue-700' : s === 'confirmed' ? 'text-green-700' : 'text-gray-500';

function LineRow({ line, today, showDay }: { line: Line; today: string; showDay?: boolean }) {
  return (
    <div className="px-5 py-2.5 flex items-start justify-between gap-3">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          {line.status === 'todo'
            ? <Clock className="h-3.5 w-3.5 text-gray-300 flex-shrink-0" />
            : <CheckCircle className={`h-3.5 w-3.5 flex-shrink-0 ${line.status === 'confirmed' ? 'text-green-500' : line.status === 'sent' ? 'text-blue-500' : 'text-amber-500'}`} />}
          <p className="text-sm font-semibold text-gray-900 break-words leading-snug">{line.title}</p>
        </div>
        {line.words && <p className="text-xs text-gray-500 ml-5 break-words">{line.words}</p>}
        {showDay && <p className="text-[11px] text-gray-500 ml-5">{line.day === today ? 'Today' : fmtShortDate(line.day)}</p>}
      </div>
      <div className="text-right flex-shrink-0">
        <p className={`text-sm font-bold tabular-nums ${statusCls(line.status)}`}>${line.amount.toFixed(2)}</p>
        <p className="text-[10px] uppercase tracking-wide text-gray-500">{STATUS_WORD[line.status]}</p>
      </div>
    </div>
  );
}

/**
 * Pay & score — everything about money and how the work is judged, off the
 * field page (owner, Nov 4 2026: "have all the pay, what you're judged on etc
 * on a different page; have a page that is purely field"). The figures and the
 * pay sheet are the ones the dashboard used to carry; the field page keeps the
 * day timer and the stops.
 */
export default function CrewPay() {
  const { user } = useAuth();
  const today = todayISO();
  const [weekPayOpen, setWeekPayOpen] = useState(false);

  // Escape closes the pay sheet, like the backdrop tap already does.
  useEffect(() => {
    if (!weekPayOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setWeekPayOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [weekPayOpen]);

  // All upcoming jobs (for pay totals and today's list)
  const { data: allJobsData } = useQuery(['crew-all-jobs'], () =>
    api.get('/jobs').then(r => r.data.jobs as any[])
  );
  // Pay ledger — owed vs sent this period (read-only; Michael pays outside the app)
  const { data: payMe, isLoading: payLoading, isError: payError } = useQuery(['crew-pay-me'], () =>
    api.get('/crew-pay/me').then(r => r.data), { refetchInterval: 60_000 }
  );

  const allJobs = allJobsData || [];
  // The span the pay card counts: the ledger's own period when it answers (the Mon–Sun week, or the
  // calendar month once he pays monthly), else this week.
  const monthly = payMe?.period?.mode === 'monthly';
  const span = monthly ? 'month' : 'week';
  const week: { start: string; end: string } = payMe?.period?.start && payMe?.period?.end ? { start: payMe.period.start, end: payMe.period.end } : weekRange();
  const periodWeeks: string[] = payMe?.period ? (payMe.period.weeks || [payMe.period.start]) : [];

  const todayJobs = inWorkingOrder(allJobs.filter(j => j.scheduled_date === today && j.status !== 'skipped'));

  const weekJobs = inWorkingOrder(allJobs.filter(j =>
    j.scheduled_date >= week.start &&
    j.scheduled_date <= week.end &&
    j.status !== 'skipped'
  ));



  // ONE earned rule, shared with Schedule → Done (utils/crewEarned): a
  // completion counts once the clock proves it (or pay was pinned flat).
  const todaySplit = splitEarned(todayJobs);
  const todayEarned  = todaySplit.earned;
  const todayPending = todaySplit.pending;
  const todayDone = todayJobs.filter(j => j.status === 'complete').length;
  const hourlyRate = allJobs.find(j => j.crew_hourly_rate_snapshot)?.crew_hourly_rate_snapshot ?? null;

  // The "no time" flag is about the record, not the money: it still reads the schedule.
  const weekSplit = splitEarned(weekJobs);
  const weekWarning = notCountedWarning(weekSplit.notCounted, weekSplit.notCountedPay);
  // Stops finished this week that are theirs — one credited to someone else ("Who worked these?") is not counted, as its pay is not.
  const weekDone = weekJobs.filter(j => j.status === 'complete' && j.credited_to_me !== false).length;
  const labelOf = (j: any) => placeLabel(j.property_label) || j.address?.split(',')[0] || j.customer_name || j.title || 'Stop';

  // ONE this-week figure, the pay ledger's (owner, Oct 2 2026: "why is there 2
  // different owed numbers"). Earned = owed + sent + confirmed this Mon–Sun week:
  // every stop at its estimate + paid drive, tips, bonuses, the rest of a part-paid
  // line — exactly what Crew Pay shows the office. "Still to do" is what the
  // ledger will write for the stops left this week. A login with no ledger
  // (the view-only account) falls back to the schedule's estimates and says so.
  // While Crew Pay is still loading the card shows a dash: the estimate first and
  // the ledger's figure a moment later would be the two numbers all over again.
  const ledgerLoading = payLoading;
  const ledgerReady = !!payMe?.period;
  const tp = payMe?.this_period || {};
  const weekTotal = ledgerReady ? Number(tp.total || 0) : weekSplit.earned;
  const weekOwed = Number(tp.owed || 0), weekSent = Number(tp.sent || 0), weekConfirmed = Number(tp.confirmed || 0);
  const weekPending = ledgerReady ? Number(tp.pending || 0) : weekSplit.pending;
  const lines: Line[] = ledgerReady
    ? (payMe.entries || [])
        .filter((e: any) => periodWeeks.includes(e.pay_period_start))   // the weeks of the period (one, or the month's)
        .map((e: any): Line => ({ key: `e${e.id}`, day: String(e.scheduled_date || today).slice(0, 10), title: e.title || e.customer_name || 'Pay', words: e.words || '', amount: Number(e.amount || 0), status: e.status }))
    : ledgerLoading ? []
    : weekJobs.filter(j => j.status === 'complete')
        .map((j): Line => ({ key: `j${j.id}`, day: j.scheduled_date, title: labelOf(j), words: j.estimated_minutes_snapshot ? `${j.estimated_minutes_snapshot} min estimate` : '', amount: payOf(j), status: 'est' }));
  const todo: Line[] = ledgerReady
    ? (payMe.pending_jobs || [])
        .map((p: any): Line => ({ key: `p${p.job_id}`, day: p.scheduled_date, title: p.customer_name || p.address || 'Stop', words: p.words || '', amount: Number(p.amount || 0), status: 'todo' }))
    : ledgerLoading ? []
    : weekJobs.filter(j => j.status !== 'complete' && payOf(j) > 0)
        .map((j): Line => ({ key: `j${j.id}`, day: j.scheduled_date, title: labelOf(j), words: j.estimated_minutes_snapshot ? `${j.estimated_minutes_snapshot} min estimate` : '', amount: payOf(j), status: 'todo' }));
  const lineDays = Array.from(new Set(lines.map(l => l.day))).sort();
  const lastWeek = ledgerReady ? payMe.last_period : null;
  const hasLastWeek = !!lastWeek && (Number(lastWeek.owed) > 0 || Number(lastWeek.sent) > 0 || Number(lastWeek.confirmed) > 0);

  // ── Efficiency: computed from ALL completed jobs (not just today) ──────────
  // Derived minutes are copied from the estimate, so they would score exactly
  // 100% and flatten the real signal. Measured jobs only.
  // A stop credited to someone else ("Who worked these?") is not theirs to be
  // scored on. A solo stop is scored against the crew-adjusted estimate the
  // server sends (effective_estimated_minutes), the same as the office sees.
  const estOf = (j: any) => Number(j.effective_estimated_minutes) > 0 ? Number(j.effective_estimated_minutes) : Number(j.estimated_minutes_snapshot);
  const completedWithTime = allJobs.filter(j =>
    j.status === 'complete' &&
    j.credited_to_me !== false &&
    Number(j.estimated_minutes_snapshot) > 0 &&
    Number(j.actual_minutes) >= 5 &&
    !j.actual_minutes_is_derived
  );
  const totalEst = completedWithTime.reduce((s, j) => s + estOf(j), 0);
  const totalAct = completedWithTime.reduce((s, j) => s + Number(j.actual_minutes), 0);
  const allTimeEff = totalAct > 0 ? Math.round((totalEst / totalAct) * 100) : null;
  const allTimeMinDiff = totalEst - totalAct;

  const eff = allTimeEff;
  const effLabel =
    eff == null ? null
    : eff >= 110 ? 'Crushing it 🚀'
    : eff >= 100 ? 'Ahead of schedule ✅'
    : eff >= 90  ? 'On pace 👍'
    : eff >= 80  ? 'Running a bit behind'
    : 'Running behind — hang in there';
  const effBg =
    eff == null ? 'bg-gray-50 border-gray-200'
    : eff >= 90  ? 'bg-green-50 border-green-200'
    : eff >= 80  ? 'bg-yellow-50 border-yellow-200'
    : 'bg-red-50 border-red-200';
  const effText =
    eff == null ? 'text-gray-500'
    : eff >= 90  ? 'text-green-700'
    : eff >= 80  ? 'text-yellow-700'
    : 'text-red-700';

  return (
    <div className="space-y-5">


      {/* ── Efficiency Score (all-time) ── */}
      <div className={`rounded-xl border p-4 ${effBg}`}>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1.5">
            <Zap className={`h-4 w-4 ${effText}`} />
            <p className={`text-xs font-bold uppercase tracking-wide ${effText}`}>Efficiency Score</p>
          </div>
          {completedWithTime.length > 0 && (
            <span className="text-xs text-gray-500">{completedWithTime.length} timed job{completedWithTime.length !== 1 ? 's' : ''} · all-time</span>
          )}
        </div>
        {eff != null ? (
          <div className="flex items-end gap-3">
            <span className={`text-5xl font-black tracking-tight ${effText}`}>{eff}%</span>
            <div className="pb-1">
              <p className={`text-sm font-bold ${effText}`}>{effLabel}</p>
              {allTimeMinDiff !== 0 && (
                <p className="text-xs text-gray-500">
                  {allTimeMinDiff > 0
                    ? `${allTimeMinDiff} min saved vs estimates`
                    : `${Math.abs(allTimeMinDiff)} min over estimates`}
                </p>
              )}
            </div>
          </div>
        ) : (
          <>
            <p className="text-4xl font-black text-gray-200 tracking-tight">—</p>
            <p className="text-xs text-gray-500 mt-1">Complete a timed job to see your score</p>
          </>
        )}
      </div>

      <div>
        <h1 className="text-2xl sm:text-3xl font-black uppercase tracking-tight text-gray-900">Pay &amp; score</h1>
        <p className="text-sm text-gray-500 mt-0.5">{user?.name?.split(' ')[0] ?? ''} · {fmtDate(today)}</p>
      </div>

      {/* This week — ONE figure, the pay ledger's. Tap for every line. */}
      <button
        onClick={() => setWeekPayOpen(true)}
        className="w-full text-left bg-white rounded-xl shadow-sm p-4 border border-brand-100 active:scale-[0.99] transition-transform"
      >
        <div className="flex items-center justify-between gap-2 mb-1">
          <p className="text-xs font-semibold text-brand-700 uppercase tracking-wide">Earned this {span}</p>
          {ledgerReady && <p className="text-[11px] text-gray-500">paid {fmtShortDate(payMe.period.due)}</p>}
        </div>
        <div className="flex items-end gap-1">
          <DollarSign className="h-5 w-5 text-green-600 mb-1" />
          <span className="text-3xl font-black text-green-700 tabular-nums leading-none">{ledgerLoading ? '—' : weekTotal.toFixed(2)}</span>
        </div>
        {ledgerLoading ? (
          <p className="text-xs text-gray-500 mt-2">Reading Crew Pay…</p>
        ) : ledgerReady ? (
          <p className="text-xs text-gray-600 mt-2 tabular-nums">
            Owed <b className="text-amber-700">${weekOwed.toFixed(2)}</b> · Sent <b className="text-blue-700">${weekSent.toFixed(2)}</b> · Confirmed <b className="text-green-700">${weekConfirmed.toFixed(2)}</b>
          </p>
        ) : (
          <p className="text-xs text-gray-500 mt-2">{payError ? "Crew Pay did not answer — the schedule's estimates for now." : "From the schedule's estimates — this login has no pay ledger."}</p>
        )}
        {!ledgerLoading && weekPending > 0 && (
          <p className="text-xs text-gray-500 mt-0.5 tabular-nums">+ ${weekPending.toFixed(2)} still to do</p>
        )}
        <p className="text-xs text-gray-500 mt-1">
          {weekDone} stop{weekDone !== 1 ? 's' : ''} done
          {hourlyRate != null ? ` · $${hourlyRate}/hr` : ''}
          {weekSplit.notCounted.length > 0 ? ` · ⚠ ${weekSplit.notCounted.length} no time` : ''}
          <span className="text-brand-400 ml-1">→ details</span>
        </p>
        {hasLastWeek && (
          <p className="text-[11px] text-gray-500 mt-2 pt-2 border-t border-gray-100 tabular-nums">
            Last {span}: owed ${Number(lastWeek.owed || 0).toFixed(0)} · sent ${Number(lastWeek.sent || 0).toFixed(0)} · confirmed ${Number(lastWeek.confirmed || 0).toFixed(0)}
          </p>
        )}
      </button>

      {/* Today — the schedule's estimates for today's stops (not clickable) */}
      <div className="bg-white rounded-xl shadow-sm p-4 border border-green-100 flex items-center justify-between gap-3">
        <div>
          <p className="text-xs font-semibold text-green-700 uppercase tracking-wide mb-1">Today · Earned</p>
          <div className="flex items-end gap-1">
            <DollarSign className="h-4 w-4 text-green-600 mb-0.5" />
            <span className="text-2xl font-bold text-green-700 tabular-nums">{todayEarned.toFixed(2)}</span>
          </div>
        </div>
        <div className="text-right">
          {todayPending > 0 && (
            <p className="text-xs text-gray-500 tabular-nums">+ ${todayPending.toFixed(2)} pending</p>
          )}
          <p className="text-xs text-gray-500 mt-0.5">
            {todayJobs.length} job{todayJobs.length !== 1 ? 's' : ''}
            {todayDone > 0 ? ` · ${todayDone} done` : ''}
          </p>
        </div>
      </div>

      {/* ── What every yard is judged on ── */}
      <JudgedOnThis />

      {/* ── This week's pay — every ledger line, then what is still to do ── */}
      {weekPayOpen && (
        <div className="fixed inset-0 z-50 flex flex-col justify-end" role="dialog" aria-modal="true" aria-label={`This ${span}'s pay`} onClick={() => setWeekPayOpen(false)}>
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/40" />
          {/* Sheet */}
          <div
            className="relative bg-white rounded-t-2xl shadow-2xl max-h-[85vh] flex flex-col"
            onClick={e => e.stopPropagation()}
          >
            {/* Handle */}
            <div className="flex justify-center pt-3 pb-1">
              <div className="w-10 h-1 rounded-full bg-gray-300" />
            </div>

            {/* Header */}
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
              <div>
                <h2 className="text-base font-bold text-gray-900">This {span}'s pay</h2>
                <p className="text-xs text-gray-500">
                  {fmtShortDate(week.start)} – {fmtShortDate(week.end)}{ledgerReady ? ` · paid ${fmtShortDate(payMe.period.due)}` : ''}
                </p>
              </div>
              <button onClick={() => setWeekPayOpen(false)} className="p-1.5 text-gray-500 hover:text-gray-600 rounded-full hover:bg-gray-100">
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Lines */}
            <div className="overflow-y-auto flex-1">
              {lines.length === 0 && todo.length === 0 ? (
                <div className="p-8 text-center text-gray-500 text-sm">{ledgerLoading ? 'Reading Crew Pay…' : `Nothing on the books this ${span} yet.`}</div>
              ) : (
                <>
                  {lineDays.map(day => (
                    <div key={day}>
                      <p className="px-5 pt-3 pb-1 text-[11px] font-bold uppercase tracking-wide text-gray-500 bg-gray-50">
                        {day === today ? 'Today' : fmtShortDate(day)}
                      </p>
                      <div className="divide-y divide-gray-100">
                        {lines.filter(l => l.day === day).map(l => <LineRow key={l.key} line={l} today={today} />)}
                      </div>
                    </div>
                  ))}
                  {todo.length > 0 && (
                    <div>
                      <p className="px-5 pt-3 pb-1 text-[11px] font-bold uppercase tracking-wide text-gray-500 bg-gray-50">
                        Still to do · ${weekPending.toFixed(2)} when finished
                      </p>
                      <div className="divide-y divide-gray-100">
                        {todo.map(l => <LineRow key={l.key} line={l} today={today} showDay />)}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Footer — the one figure and what makes it up */}
            <div className="border-t border-gray-100 px-5 py-4 bg-gray-50 space-y-1">
              {weekWarning && (
                <p className="text-xs text-amber-600 font-medium">{weekWarning}</p>
              )}
              <div className="flex items-baseline justify-between gap-3 tabular-nums">
                <p className="text-sm font-bold text-green-700">Earned {ledgerLoading ? '—' : `$${weekTotal.toFixed(2)}`}</p>
                {!ledgerLoading && weekPending > 0 && (
                  <p className="text-xs text-gray-500">Still to do ${weekPending.toFixed(2)}</p>
                )}
              </div>
              {ledgerReady && (
                <p className="text-[11px] text-gray-500 tabular-nums">Owed ${weekOwed.toFixed(2)} · Sent ${weekSent.toFixed(2)} · Confirmed ${weekConfirmed.toFixed(2)}</p>
              )}
              <p className="text-[11px] text-gray-500">
                {ledgerReady
                  ? 'What Crew Pay says: stops at estimate + drive, tips, bonuses, the rest of a part-paid line.'
                  : `${weekDone} of ${weekJobs.length} jobs done`}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
